import { Card, Group, Skeleton, Stack, Text } from '@mantine/core'
import { IconArrowDown, IconArrowUp } from '@tabler/icons-react'
import { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { useGetDeviceMetrics } from '@/queries/deviceQueries'
import { formatSize } from '@/utils/file'
import { computeNetworkDelta } from './computeNetworkDelta'

const SENT_KEY = 'net_io_bytes_sent_total'
const RECV_KEY = 'net_io_bytes_recv_total'

type DeviceNetworkStatsProps = {
  deviceId: string
  title: ReactNode
  rangeLabel: ReactNode
  startTs: number
  endTs: number
  interval?: number
}

export const DeviceNetworkStats = ({
  deviceId,
  title,
  rangeLabel,
  startTs,
  endTs,
  interval
}: DeviceNetworkStatsProps) => {
  const { data, isLoading, isError } = useGetDeviceMetrics(deviceId, {
    keys: `${SENT_KEY},${RECV_KEY}`,
    startTs,
    endTs,
    agg: interval ? 'MAX' : 'NONE',
    interval
  })

  if (isLoading) {
    return <Skeleton height={88} />
  }

  if (isError) {
    return (
      <PlatformAlert variant="error">
        <FormattedMessage id="deployments.details.observability.error" />
      </PlatformAlert>
    )
  }

  const sentDelta = computeNetworkDelta(data?.[SENT_KEY] ?? [])
  const recvDelta = computeNetworkDelta(data?.[RECV_KEY] ?? [])

  if (sentDelta === null && recvDelta === null) {
    return (
      <Card padding="md" withBorder>
        <Stack gap="md">
          <Text size="sm" fw={600}>
            {title}
          </Text>
          <Text c="dimmed" size="xs">
            {rangeLabel}
          </Text>
          <Text c="dimmed" size="sm">
            <FormattedMessage id="deployments.details.observability.noData" />
          </Text>
        </Stack>
      </Card>
    )
  }

  return (
    <Card padding="md" withBorder>
      <Stack gap="xs">
        <Text size="sm" fw={600}>
          {title}
        </Text>
        <Text c="dimmed" size="sm">
          {rangeLabel}
        </Text>
        <Group gap="xl">
          <Group gap="xs">
            <IconArrowUp size={24} color="var(--mantine-color-blue-6)" />
            <Text size="lg" fw={500}>
              {sentDelta !== null ? formatSize(sentDelta, 'b') : '—'}
            </Text>
            <Text size="sm" c="dimmed">
              <FormattedMessage id="deployments.details.observability.network.sent" />
            </Text>
          </Group>
          <Group gap="xs">
            <IconArrowDown size={24} color="var(--mantine-color-teal-6)" />
            <Text size="lg" fw={500}>
              {recvDelta !== null ? formatSize(recvDelta, 'b') : '—'}
            </Text>
            <Text size="sm" c="dimmed">
              <FormattedMessage id="deployments.details.observability.network.received" />
            </Text>
          </Group>
        </Group>
      </Stack>
    </Card>
  )
}
